import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BundleFlightData } from '../../modules/FlightDetails/flightSlice';
import { OptionalFilter } from '../../components/SideFilters/interface';
import {
  FlightAsyncState,
  SupplierError,
  AsyncSearchStatus,
} from './flightAsyncTypes';

const initialState: FlightAsyncState = {
  status: AsyncSearchStatus.Idle,
  flights: [],
  expectedSuppliers: 0,
  returnedSuppliers: 0,
  supplierErrors: [],
  optionalFilters: [], // Empty array instead of null so filters render immediately with skeletons
  error: null,
  searchStartTime: null,
  searchEndTime: null,
  totalCount: 0,
  successCount: 0,
  errorCount: 0,
};

export const flightAsyncSlice = createSlice({
  name: 'flightAsync',
  initialState,
  reducers: {
    startSSESearch: (state) => {
      state.status = AsyncSearchStatus.LoadingAll;
      state.flights = [];
      state.returnedSuppliers = 0;
      state.supplierErrors = [];
      state.error = null;
      state.optionalFilters = []; // Empty array so filters can render with skeletons while loading
      state.searchStartTime = Date.now();
      state.searchEndTime = null;
      state.totalCount = 0;
      state.successCount = 0;
      state.errorCount = 0;
    },

    setExpectedSuppliers: (state, action: PayloadAction<number>) => {
      state.expectedSuppliers = action.payload;
    },

    receiveFirstChunk: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      state.status = AsyncSearchStatus.FirstChunkReceived;
      state.flights.push(...action.payload.flights);
      state.returnedSuppliers += 1;
    },

    receiveChunk: (
      state,
      action: PayloadAction<{
        flights: BundleFlightData[];
        ts_id: number;
        ts_type?: string;
      }>
    ) => {
      if (
        state.status === AsyncSearchStatus.FirstChunkReceived ||
        state.status === AsyncSearchStatus.LoadingAll
      ) {
        state.status = AsyncSearchStatus.Streaming;
      }
      state.flights.push(...action.payload.flights);
      state.returnedSuppliers += 1;
    },

    receiveSupplierError: (state, action: PayloadAction<SupplierError>) => {
      state.supplierErrors.push(action.payload);
      state.returnedSuppliers += 1;
    },

    updateOptionalFilters: (state, action: PayloadAction<OptionalFilter[]>) => {
      state.optionalFilters = action.payload;
    },

    updateProgress: (
      state,
      action: PayloadAction<{
        totalCount: number;
        successCount: number;
        errorCount: number;
      }>
    ) => {
      state.totalCount = action.payload.totalCount;
      state.successCount = action.payload.successCount;
      state.errorCount = action.payload.errorCount;
    },

    completeSSESearch: (state) => {
      state.status = AsyncSearchStatus.Complete;
      state.searchEndTime = Date.now();
    },

    errorSSESearch: (state, action: PayloadAction<string>) => {
      state.status = AsyncSearchStatus.Error;
      state.error = action.payload;
      state.searchEndTime = Date.now();
    },

    resetAsyncSearch: (state) => {
      return initialState;
    },
  },
});

export const {
  startSSESearch,
  setExpectedSuppliers,
  receiveFirstChunk,
  receiveChunk,
  receiveSupplierError,
  updateOptionalFilters,
  updateProgress,
  completeSSESearch,
  errorSSESearch,
  resetAsyncSearch,
} = flightAsyncSlice.actions;

export default flightAsyncSlice.reducer;
